import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { personalKbApi, type PersonalFile, PersonalKbError } from "../lib/personalKbApi";
import { FeishuConnectionSection } from "./FeishuConnectionSection";
import { IconFile, IconRefresh } from "../components/icons";
import "../styles/personal-kb.css";

/**
 * 我的资料库 panel (P4 我的资料 · P4a/4b). A per-user modal: upload private files
 * or pick Feishu docs into your own library so the assistant answers from your
 * materials — only you can retrieve them. Rows are grouped by source: Feishu docs
 * carry a 飞书 badge + snapshot age + a refresh control (single + "sync all"),
 * uploads carry an 上传 badge. Copy is plain; no implementation terms surface.
 */
export function PersonalKbPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const [files, setFiles] = useState<PersonalFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // docIds currently re-syncing (spinner) and ones synced this session (✓ label).
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const [justSynced, setJustSynced] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setFiles(await personalKbApi.list());
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Feishu docs (refreshable, badge 飞书) vs everything else (uploads, badge 上传).
  const feishuFiles = useMemo(() => files.filter((f) => f.sourceKind === "feishu"), [files]);
  const uploadFiles = useMemo(() => files.filter((f) => f.sourceKind !== "feishu"), [files]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange.
    e.target.value = "";
    if (file === undefined) return;
    setErr("");
    setBusy(true);
    try {
      const row = await personalKbApi.upload(file);
      // Optimistically place the new row on top; re-upload replaces in place.
      setFiles((prev) => [row, ...prev.filter((f) => f.docId !== row.docId)]);
    } catch (e2) {
      setErr(e2 instanceof PersonalKbError ? e2.message : "上传没成功，稍后再试。");
    }
    setBusy(false);
  }

  async function onRemove(docId: string): Promise<void> {
    setBusy(true);
    const ok = await personalKbApi.remove(docId);
    if (ok) setFiles((prev) => prev.filter((f) => f.docId !== docId));
    setBusy(false);
  }

  /** Re-sync Feishu docs from their originals. `docIds` omitted = every Feishu row. */
  async function onSync(docIds?: string[]): Promise<void> {
    const targets = docIds ?? feishuFiles.map((f) => f.docId);
    if (targets.length === 0) return;
    setErr("");
    setSyncing((s) => new Set([...s, ...targets]));
    try {
      const { files: updated, skipped } = await personalKbApi.feishuRefresh(docIds);
      // Merge the re-synced rows back in place (fresh chunkCount + uploadedAt).
      const byId = new Map(updated.map((u) => [u.docId, u]));
      setFiles((prev) => prev.map((f) => byId.get(f.docId) ?? f));
      setJustSynced((s) => new Set([...s, ...updated.map((u) => u.docId)]));
      if (skipped.length > 0) setErr(`${skipped.length} 份没能同步（可能已删除或没权限）。`);
    } catch (e) {
      setErr(e instanceof PersonalKbError ? e.message : "同步没成功，稍后再试。");
    } finally {
      setSyncing((s) => {
        const next = new Set(s);
        targets.forEach((id) => next.delete(id));
        return next;
      });
    }
  }

  const syncingAll = feishuFiles.length > 0 && feishuFiles.every((f) => syncing.has(f.docId));

  function renderFeishuRow(f: PersonalFile): JSX.Element {
    const isSyncing = syncing.has(f.docId);
    const synced = justSynced.has(f.docId) && !isSyncing;
    return (
      <li className="pkb-row" key={f.docId}>
        <span className="pkb-ic pkb-ic-fs" aria-hidden="true">
          <IconFile />
        </span>
        <div className="pkb-meta">
          <div className="pkb-name">
            <span className="pkb-title">{f.title}</span>
            <span className="pkb-src pkb-src-fs">飞书</span>
          </div>
          <div className="pkb-sub">
            {isSyncing ? (
              <span className="pkb-sub-sync">同步中…</span>
            ) : synced ? (
              <span className="pkb-sub-ok">✓ 刚刚同步</span>
            ) : (
              `快照于 ${relTime(f.uploadedAt)}`
            )}
          </div>
        </div>
        <div className="pkb-acts">
          <button
            className={`pkb-sync${isSyncing ? " spin" : ""}`}
            type="button"
            disabled={busy || isSyncing}
            title="刷新到最新"
            aria-label="刷新到最新"
            onClick={() => void onSync([f.docId])}
            data-testid="pkb-sync"
          >
            <IconRefresh />
          </button>
          <button
            className="pkb-del"
            type="button"
            disabled={busy || isSyncing}
            onClick={() => void onRemove(f.docId)}
          >
            删除
          </button>
        </div>
      </li>
    );
  }

  function renderUploadRow(f: PersonalFile): JSX.Element {
    return (
      <li className="pkb-row" key={f.docId}>
        <span className="pkb-ic" aria-hidden="true">
          <IconFile />
        </span>
        <div className="pkb-meta">
          <div className="pkb-name">
            <span className="pkb-title">{f.title}</span>
            <span className="pkb-src pkb-src-up">上传</span>
          </div>
          <div className="pkb-sub">
            {f.chunkCount > 0 ? `${f.chunkCount} 段索引 · 答题自动用` : "空文件"}
          </div>
        </div>
        <button className="pkb-del" type="button" disabled={busy} onClick={() => void onRemove(f.docId)}>
          删除
        </button>
      </li>
    );
  }

  return (
    <div className="pkb-modal-bg" onClick={onClose}>
      <div className="pkb-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="pkb-head">
          <h2>我的资料库</h2>
          <button className="pkb-x" type="button" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>
        <div className="pkb-body">
          <div className="pkb-reassure">
            上传你自己的文件，小环答题时会按你的资料回答。只有你能检索到，别人看不到。
          </div>
          <FeishuConnectionSection onPicked={() => void refresh()} />
          <div className="pkb-upload-head">上传文件</div>
          <input
            ref={fileRef}
            type="file"
            accept=".md,.txt,.markdown,.text"
            className="pkb-file-input"
            onChange={(e) => void onPick(e)}
          />
          <button
            className="pkb-upload"
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            data-testid="pkb-upload"
          >
            {busy ? "处理中…" : "上传文件"}
          </button>
          {err !== "" && (
            <div className="pkb-err" role="alert">
              {err}
            </div>
          )}

          {loading ? (
            <div className="pkb-note">加载中…</div>
          ) : files.length === 0 ? (
            <div className="pkb-empty">还没有资料。上传后就能在对话里用上。</div>
          ) : (
            <>
              {feishuFiles.length > 0 && (
                <>
                  <div className="pkb-grouphd">
                    <span className="pkb-groupl">飞书文档</span>
                    <button
                      className={`pkb-syncall${syncingAll ? " spin" : ""}`}
                      type="button"
                      disabled={busy || syncingAll}
                      onClick={() => void onSync()}
                      data-testid="pkb-syncall"
                    >
                      <IconRefresh />
                      同步全部飞书
                    </button>
                  </div>
                  <ul className="pkb-list" data-testid="pkb-list-feishu">
                    {feishuFiles.map(renderFeishuRow)}
                  </ul>
                </>
              )}
              {uploadFiles.length > 0 && (
                <>
                  {feishuFiles.length > 0 && (
                    <div className="pkb-grouphd">
                      <span className="pkb-groupl">上传文件</span>
                    </div>
                  )}
                  <ul className="pkb-list" data-testid="pkb-list">
                    {uploadFiles.map(renderUploadRow)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Human-friendly age of a snapshot timestamp ("刚刚" / "3 天前" / a date). */
function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "未知时间";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "刚刚";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}
